/**
 * CodeBoxes — the S1 code-drop input rendered over the pure lib/codebox state.
 * 4 boxes for night codes (BLG- prefix rendered, not typed), 6 for OTP.
 * Auto-advance, paste-detect, backspace walk; SMS autofill via
 * autocomplete="one-time-code" on the focused box (digits mode).
 */
import { useEffect, useRef, useState } from "react";
import {
  backspace,
  codeValue,
  emptyCode,
  inputChar,
  isComplete,
  paste,
  type CodeBoxState,
  type CodeCharset,
} from "../lib/codebox";

export interface CodeBoxesProps {
  length: number;
  charset: CodeCharset;
  /** Rendered (never typed) prefix chip, e.g. "BLG-". */
  prefix?: string;
  /** Fires when every box holds a value. */
  onComplete?: (code: string) => void;
  label: string;
  disabled?: boolean;
}

export function CodeBoxes({ length, charset, prefix, onComplete, label, disabled }: CodeBoxesProps) {
  const [state, setState] = useState<CodeBoxState>(() => emptyCode(length, charset));
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const completedFor = useRef<string | null>(null);

  useEffect(() => {
    refs.current[state.focusIndex]?.focus();
  }, [state.focusIndex]);

  useEffect(() => {
    if (!isComplete(state)) {
      completedFor.current = null;
      return;
    }
    const code = codeValue(state);
    if (completedFor.current === code) return; // auto-submit once per code
    completedFor.current = code;
    onComplete?.(code);
  }, [state, onComplete]);

  return (
    <div className="pl-codeboxes" role="group" aria-label={label}>
      {prefix !== undefined ? <span className="pl-codeboxes-prefix">{prefix}</span> : null}
      {state.values.map((value, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="pl-codebox"
          type="text"
          inputMode={charset === "digits" ? "numeric" : "text"}
          autoComplete={charset === "digits" && i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={value}
          disabled={disabled === true}
          aria-label={`${label} character ${i + 1}`}
          onFocus={() => setState((s) => ({ ...s, focusIndex: i }))}
          onChange={(e) => {
            const text = e.target.value;
            setState((s) =>
              text.length > 1 ? paste({ ...s, focusIndex: i }, text) : inputChar({ ...s, focusIndex: i }, text),
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              setState((s) => backspace({ ...s, focusIndex: i }));
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text");
            setState((s) => paste(s, text));
          }}
        />
      ))}
    </div>
  );
}
